import { SerializedAndroidGooglePayPaymentSuccessResponse } from '@eventbrite/checkout-native-api';
import logger from '@eventbrite/client-logger';
import { $FixMe } from '@eventbrite/ts-utils';
import {
    GOOGLE_PAY_MERCHANT_NAME,
    GOOGLE_PAY_SHARED_PAYLOAD,
} from '../constants/googlePay';
import { ContactFields } from '../models/order';
import { getCurrency } from '../selectors/event';
import { getTotalAmount } from '../selectors/order';
import { getContactInformation } from '../selectors/user';
import { CheckoutState } from '../state';

export const canMakePaymentsWithGooglePay = (isProd: boolean) => {
    const environment = isProd ? 'PRODUCTION' : 'TEST';

    return new google.payments.api.PaymentsClient({
        environment,
    }).isReadyToPay(GOOGLE_PAY_SHARED_PAYLOAD);
};

export const isGooglePayIntegrationSupportedInNativeApp = () =>
    window.android?.isGooglePayIntegrationSupported?.();

export const isReadyToPayInAndroidApp = (
    successCallback: (canMakePayments: boolean) => void,
    errorCallback: (error: any) => void,
) => {
    window._isReadyToPaySuccessCallback = successCallback;
    window._isReadyToPayErrorCallback = errorCallback;

    try {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        window.android!.isReadyToPay(
            true,
            '((isReady) => {' +
                '    window._isReadyToPaySuccessCallback(isReady);' +
                '    delete window._isReadyToPaySuccessCallback;' +
                '})',
            '((errorMessage) => {' +
                '    window._isReadyToPayErrorCallback(errorMessage);' +
                '    delete window._isReadyToPayErrorCallback;' +
                '})',
        );
    } catch (e) {
        logger.error(e);
    }
};

export const makePaymentInAndroidApp = (
    state: CheckoutState,
    successCallback: (
        param: SerializedAndroidGooglePayPaymentSuccessResponse,
    ) => void,
    errorCallback: (error: $FixMe) => void,
) => {
    const totalAmount = getTotalAmount(state);
    const currency = getCurrency(state);

    window._makePaymentSuccessCallback = successCallback;
    window._makePaymentErrorCallback = errorCallback;

    try {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        window.android!.makePayment(
            `${totalAmount}`,
            currency || 'USD',
            GOOGLE_PAY_MERCHANT_NAME,
            '((googlePayBraintreeNonce) => {' +
                '    window._makePaymentSuccessCallback(googlePayBraintreeNonce);' +
                '    delete window._makePaymentSuccessCallback;' +
                '})',
            '((errorMessage) => {' +
                '    window._makePaymentErrorCallback(errorMessage);' +
                '    delete window._makePaymentErrorCallback;' +
                '})',
        );
    } catch (e) {
        logger.error(e);
    }
};

const getNameFromBuyerDetails = (fullName?: string) => {
    const DEFAULT_VALUE = '-';

    if (!fullName) {
        return { firstName: DEFAULT_VALUE, lastName: DEFAULT_VALUE };
    }

    const firstBlankIndex = fullName.indexOf(' ');
    return firstBlankIndex === -1
        ? { firstName: fullName, lastName: DEFAULT_VALUE }
        : {
              firstName: fullName.substring(0, firstBlankIndex),
              lastName: fullName.substring(firstBlankIndex + 1),
          };
};

export const getGooglePayBuyerData = (
    buyerDetails: Pick<google.payments.api.PaymentData, 'email'> & {
        paymentMethodData?: {
            info?: {
                billingAddress?: {
                    name?: NonNullable<
                        NonNullable<
                            NonNullable<
                                google.payments.api.PaymentData['paymentMethodData']
                            >['info']
                        >['billingAddress']
                    >['name'];
                };
            };
        };
    },
    state: CheckoutState,
) => {
    const contactInfoFromState = getContactInformation(state);
    const nameFromBuyerDetails = getNameFromBuyerDetails(
        buyerDetails.paymentMethodData?.info?.billingAddress?.name,
    );

    return {
        [ContactFields.FIRST_NAME]:
            contactInfoFromState.firstName || nameFromBuyerDetails.firstName,
        [ContactFields.LAST_NAME]:
            contactInfoFromState.lastName || nameFromBuyerDetails.lastName,
        [ContactFields.EMAIL]: contactInfoFromState.email || buyerDetails.email,
    };
};
